/**
 * Front Desk — Nueva cita SIN salir del calendario (Persona 360, Fase 3).
 *
 * Tres decisiones: PERSONA -> SERVICIO -> HORARIO. El sistema resuelve el resto
 * (duración, holgura, recurso, disponibilidad) con el motor único de la Fase 2.
 * Si la persona no existe, se da de alta desde aquí (nombre + teléfono) creando
 * el registro CRM correcto por debajo (sin base de pacientes paralela).
 *
 * Vocabulario tropicalizado: Cliente/Paciente, Servicio/Consulta, etc. salen del
 * diccionario de la vertical (GET /tenant/business/dictionary), no hardcodeados.
 */
import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X, Search, UserPlus, Star, Sparkles, ArrowLeft, Check, Clock, Package } from 'lucide-react';

export default function NuevaCitaDrawer({ open, onClose, onCreated, fecha, personaInicial }) {
  const [dict, setDict] = useState({});
  const [services, setServices] = useState([]);
  const [paso, setPaso] = useState('persona');   // persona | servicio | horario
  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [persona, setPersona] = useState(null);   // {lead_id, display_name, ...}
  const [servicio, setServicio] = useState(null);
  const [slots, setSlots] = useState([]);
  const [cargandoSlots, setCargandoSlots] = useState(false);
  // recursos (Fase 4): auto-elige profesional + espacio, con opción de cambiar
  const [slotSel, setSlotSel] = useState(null);
  const [recursos, setRecursos] = useState(null);
  const [profSel, setProfSel] = useState(null);   // {resource_id, user_id, nombre}
  const [espSel, setEspSel] = useState(null);
  const [cargandoRec, setCargandoRec] = useState(false);
  const [guardando, setGuardando] = useState(false);
  // alta rápida
  const [altaNombre, setAltaNombre] = useState('');
  const [altaTel, setAltaTel] = useState('');
  // oportunidad opcional (nunca requisito para agendar)
  const [catalogo, setCatalogo] = useState([]);
  const [creaOpp, setCreaOpp] = useState(false);
  const [oppItem, setOppItem] = useState('');
  const [oppValor, setOppValor] = useState('');
  const debRef = useRef(null);

  const L = (k, fb) => (dict?.dictionary?.[k]) || fb;

  useEffect(() => {
    if (!open) return;
    api.get('/tenant/business/dictionary').then(r => setDict(r.data || {})).catch(() => {});
    api.get('/tenant/agenda/servicios-agendables').then(r => setServices(r.data?.items || [])).catch(() => {});
    api.get('/tenant/catalog/items').then(r => setCatalogo(Array.isArray(r.data) ? r.data : (r.data?.items || []))).catch(() => {});
  }, [open]);

  // reset al abrir
  useEffect(() => {
    if (open) {
      setBusca(''); setResultados([]);
      setServicio(null); setSlots([]); setAltaNombre(''); setAltaTel('');
      setCreaOpp(false); setOppItem(''); setOppValor('');
      setSlotSel(null); setRecursos(null); setProfSel(null); setEspSel(null);
      // Si viene una persona (p. ej. desde el panel 360), saltamos a servicio.
      if (personaInicial && (personaInicial.lead_id || personaInicial.id)) {
        setPersona({
          lead_id: personaInicial.lead_id || personaInicial.id,
          display_name: personaInicial.display_name,
          num_visitas: personaInicial.num_visitas || 0,
          es_recurrente: !!personaInicial.es_recurrente,
        });
        setPaso('servicio');
      } else {
        setPersona(null); setPaso('persona');
      }
    }
  }, [open, personaInicial]);

  // búsqueda con debounce
  useEffect(() => {
    if (!open || paso !== 'persona') return undefined;
    if (busca.trim().length < 2) { setResultados([]); return undefined; }
    setBuscando(true);
    clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      try {
        const r = await api.get('/tenant/crm/personas/buscar', { params: { q: busca.trim() } });
        setResultados(r.data?.personas || []);
      } catch { setResultados([]); }
      setBuscando(false);
    }, 280);
    return () => clearTimeout(debRef.current);
  }, [busca, open, paso]);

  async function altaRapida() {
    const nombre = altaNombre.trim() || busca.trim();
    if (!nombre) { toast.error('Escribe al menos el nombre'); return; }
    setGuardando(true);
    try {
      const { data } = await api.post('/tenant/crm/leads', {
        display_name: nombre,
        phone: (altaTel.trim() || (/\d{6,}/.test(busca) ? busca.trim() : '')) || null,
      });
      const leadId = data.id || data.lead?.id;
      setPersona({ lead_id: leadId, display_name: nombre, num_visitas: 0, es_recurrente: false });
      setPaso('servicio');
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'No se pudo crear');
    }
    setGuardando(false);
  }

  async function elegirServicio(s) {
    setServicio(s);
    // El servicio ES del catálogo: si el negocio quiere, la oportunidad ya
    // queda ligada a ese mismo servicio (valor sugerido de su precio).
    if (s.catalog_item_id) { setOppItem(s.catalog_item_id); if (s.price_min != null) setOppValor(String(s.price_min)); }
    setPaso('horario');
    setCargandoSlots(true);
    try {
      const now = new Date();
      const desde = fecha ? new Date(fecha + 'T00:00:00') : now;
      const hasta = new Date((fecha ? new Date(fecha) : now).getTime() + 14 * 864e5);
      const r = await api.get('/tenant/agenda/availability', {
        params: {
          // service_id de AGENDA (duración/recursos); puede no existir si el
          // servicio del catálogo aún no tiene configuración de agenda.
          ...(s.agenda_service_id ? { service_id: s.agenda_service_id } : {}),
          duration_minutes: s.duration_minutes || 60,
          from_dt: desde.toISOString(),
          to_dt: hasta.toISOString(),
        },
      });
      // Cualquier disponibilidad, no solo hoy: se toman los huecos reales de
      // todo el rango (free_slots) para poder elegir cualquier día.
      setSlots(r.data?.free_slots || r.data?.suggested_slots || []);
    } catch { setSlots([]); }
    setCargandoSlots(false);
  }

  // Fase 4: elegido el hueco, se resuelven profesional + espacio (auto, cambiables)
  async function elegirSlot(slot) {
    setSlotSel(slot);
    setPaso('confirmar');
    setRecursos(null); setProfSel(null); setEspSel(null);
    // Solo hay recursos que resolver si el servicio del catálogo tiene
    // configuración de agenda (agenda_service). Si no, se agenda sin recurso.
    if (!servicio?.agenda_service_id) { setRecursos({ profesionales: [], espacios: [] }); return; }
    setCargandoRec(true);
    try {
      const r = await api.get('/tenant/agenda/recursos-de-servicio', {
        params: {
          service_id: servicio.agenda_service_id,
          starts_at: slot.starts_at_iso,
          duration_minutes: servicio?.duration_minutes || 60,
        },
      });
      setRecursos(r.data);
      setProfSel(r.data?.auto?.profesional || null);
      setEspSel(r.data?.auto?.espacio || null);
    } catch { setRecursos({ profesionales: [], espacios: [] }); }
    setCargandoRec(false);
  }

  async function confirmar(slot) {
    setGuardando(true);
    try {
      await api.post('/tenant/agenda/appointments', {
        crm_lead_id: persona.lead_id,
        service_id: servicio?.agenda_service_id || null,
        starts_at: slot.starts_at_iso,
        duration_minutes: servicio?.duration_minutes || 60,
        ...(profSel?.user_id ? { assigned_to_user_id: profSel.user_id } : {}),
        ...(espSel?.resource_id ? { resource_id: espSel.resource_id } : {}),
      });
      // Oportunidad opcional: solo si la recepción la marcó. Agendar nunca la exige.
      if (creaOpp && oppItem) {
        const it = catalogo.find(i => i.id === oppItem);
        try {
          await api.post('/tenant/crm/deals', {
            crm_lead_id: persona.lead_id,
            title: `Oportunidad: ${persona.display_name}`,
            catalog_item_id: oppItem,
            value_estimate: oppValor ? parseFloat(oppValor) : (it?.price_min ?? null),
            currency: it?.currency || 'MXN',
          });
        } catch { toast.warning('La cita quedó agendada; la oportunidad no se pudo crear.'); }
      }
      // Salud/Clínica: la cita amarra a la persona con su EXPEDIENTE. Se asegura
      // el expediente (idempotente) para que quede como paciente, no suelto como
      // lead. En otras verticales esto no aplica (separación por vertical).
      const vert = (dict?.vertical || '').toLowerCase();
      if (vert === 'clinic' || vert === 'health') {
        try { await api.post('/tenant/clinica/expedientes', { crm_lead_id: persona.lead_id }); }
        catch { /* si no hay módulo de expediente, la cita igual quedó */ }
      }
      toast.success(`${L('appointment', 'Cita')} agendada`);
      onCreated?.();
      onClose?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === 'string' ? d : d?.message) || 'No se pudo agendar');
    }
    setGuardando(false);
  }

  if (!open) return null;
  const personaLabel = L('client', 'Cliente');
  const servicioLabel = L('service', 'Servicio');

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose} data-testid="nueva-cita-drawer">
      <div className="w-full max-w-md h-full bg-white shadow-xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E2E8F0]">
          <div className="flex items-center gap-2">
            {paso !== 'persona' && (
              <button onClick={() => setPaso(paso === 'confirmar' ? 'horario' : (paso === 'horario' ? 'servicio' : 'persona'))}
                      className="text-[#64748B] hover:text-[#0F172A]" aria-label="Atrás">
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <h2 className="text-sm font-semibold text-[#0F172A]">
              Nueva {L('appointment', 'cita')}
              {persona ? ` · ${persona.display_name}` : ''}
            </h2>
          </div>
          <button onClick={onClose} aria-label="Cerrar" className="text-[#94A3B8] hover:text-[#0F172A]"><X className="w-4 h-4" /></button>
        </div>

        {/* pasos */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {paso === 'persona' && (
            <>
              <label className="text-xs font-medium text-[#64748B]">¿Quién? — busca al {personaLabel.toLowerCase()}</label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-3 text-[#94A3B8]" />
                <Input autoFocus className="pl-8" placeholder="Nombre, teléfono o email"
                       value={busca} onChange={(e) => setBusca(e.target.value)} />
              </div>
              {buscando && <p className="text-xs text-[#94A3B8]">Buscando…</p>}
              {resultados.map((p) => (
                <button key={p.lead_id} onClick={() => { setPersona(p); setPaso('servicio'); }}
                        className="w-full text-left border border-[#E2E8F0] rounded-lg p-2.5 hover:bg-[#F8FAFC]"
                        data-testid="persona-resultado">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-[#0F172A]">{p.display_name}</span>
                    {p.es_recurrente
                      ? <span className="inline-flex items-center gap-1 text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-full"><Star className="w-3 h-3" /> {p.num_visitas} visitas</span>
                      : <span className="inline-flex items-center gap-1 text-[10px] text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded-full"><Sparkles className="w-3 h-3" /> {p.num_visitas > 0 ? `${p.num_visitas} visita(s)` : 'nuevo'}</span>}
                  </div>
                  <div className="text-xs text-[#64748B]">
                    {p.phone || p.email || 'sin contacto'}
                    {p.ultima_visita ? ` · última: ${new Date(p.ultima_visita).toLocaleDateString('es-MX', { dateStyle: 'medium' })}` : ''}
                  </div>
                </button>
              ))}
              {busca.trim().length >= 2 && !buscando && resultados.length === 0 && (
                <div className="border border-dashed border-[#CBD5E1] rounded-lg p-3 space-y-2">
                  <div className="text-sm text-[#64748B]">Nadie con eso. Da de alta al {personaLabel.toLowerCase()}:</div>
                  <Input placeholder="Nombre *" value={altaNombre || busca} onChange={(e) => setAltaNombre(e.target.value)} />
                  <Input placeholder="Teléfono" value={altaTel} onChange={(e) => setAltaTel(e.target.value)} />
                  <Button className="w-full" disabled={guardando} onClick={altaRapida}>
                    <UserPlus className="w-4 h-4 mr-1" /> Crear y continuar
                  </Button>
                </div>
              )}
            </>
          )}

          {paso === 'servicio' && (
            <>
              <label className="text-xs font-medium text-[#64748B]">¿Qué {servicioLabel.toLowerCase()}?</label>
              {services.length === 0 && (
                <p className="text-sm text-[#64748B]">No hay {servicioLabel.toLowerCase()}s en tu catálogo todavía. Créalos en Catálogo.</p>
              )}
              {services.map((s) => (
                <button key={s.catalog_item_id || s.agenda_service_id} onClick={() => elegirServicio(s)}
                        className="w-full text-left border border-[#E2E8F0] rounded-lg p-2.5 hover:bg-[#F8FAFC]"
                        data-testid="servicio-opcion">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-[#0F172A]">{s.name}</div>
                    {s.price_min != null && <div className="text-xs text-[#64748B]">{s.currency || 'MXN'} {Number(s.price_min).toLocaleString('es-MX')}</div>}
                  </div>
                  <div className="text-xs text-[#64748B]">{s.duration_minutes || 60} min</div>
                </button>
              ))}
            </>
          )}

          {paso === 'horario' && (
            <>
              <label className="text-xs font-medium text-[#64748B]">¿Cuándo? — huecos reales para {servicio?.name}</label>
              {cargandoSlots && <p className="text-xs text-[#94A3B8]">Calculando disponibilidad…</p>}
              {!cargandoSlots && slots.length === 0 && (
                <p className="text-sm text-[#64748B]">No hay huecos disponibles en los próximos días para este {servicioLabel.toLowerCase()}.</p>
              )}
              {/* Cualquier disponibilidad: los huecos se agrupan POR DÍA para poder
                  elegir cualquier fecha, no solo hoy. */}
              {(() => {
                const porDia = {};
                for (const sl of slots) {
                  const k = sl.local_date || (sl.starts_at_iso || '').slice(0, 10);
                  (porDia[k] = porDia[k] || []).push(sl);
                }
                const dias = Object.keys(porDia);
                const fmtDia = (iso) => {
                  try {
                    return new Date(iso + 'T12:00:00').toLocaleDateString('es-MX',
                      { weekday: 'long', day: 'numeric', month: 'short' });
                  } catch { return iso; }
                };
                return dias.map((d) => (
                  <div key={d} className="space-y-1.5">
                    <div className="text-xs font-semibold text-[#334155] capitalize pt-1">{fmtDia(d)}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {porDia[d].map((sl) => (
                        <button key={sl.starts_at_iso} disabled={guardando} onClick={() => elegirSlot(sl)}
                                className="text-sm text-[#0F172A] border border-[#E2E8F0] rounded-lg px-2.5 py-1.5 hover:bg-emerald-50 disabled:opacity-50 inline-flex items-center gap-1"
                                data-testid="slot-opcion">
                          <Clock className="w-3 h-3 text-[#64748B]" /> {sl.local_time}
                        </button>
                      ))}
                    </div>
                  </div>
                ));
              })()}
            </>
          )}

          {paso === 'confirmar' && (
            <>
              <div className="rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] p-3 text-sm">
                <div className="font-medium text-[#0F172A]">{persona?.display_name}</div>
                <div className="text-[#64748B]">{servicio?.name} · {slotSel?.local_label_es || `${slotSel?.local_date} ${slotSel?.local_time}`}</div>
              </div>
              {cargandoRec && <p className="text-xs text-[#94A3B8]">Asignando {L('provider', 'profesional').toLowerCase()} y {(L('cabin', L('room', 'espacio'))).toLowerCase()}…</p>}

              {!cargandoRec && recursos && (recursos.profesionales?.length > 0) && (
                <div>
                  <label className="text-xs font-medium text-[#64748B]">{L('provider', 'Profesional')}</label>
                  <Select value={profSel?.resource_id || 'none'} onValueChange={(v) => setProfSel(recursos.profesionales.find(p => p.resource_id === v) || null)}>
                    <SelectTrigger className="h-8 text-xs" data-testid="conf-prof"><SelectValue placeholder="Cualquiera" /></SelectTrigger>
                    <SelectContent>
                      {recursos.profesionales.map(p => (
                        <SelectItem key={p.resource_id} value={p.resource_id} disabled={!p.libre}>
                          {p.nombre}{!p.libre ? ' (ocupado)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {!cargandoRec && recursos && (recursos.espacios?.length > 0) && (
                <div>
                  <label className="text-xs font-medium text-[#64748B]">{L('cabin', L('room', 'Espacio'))}</label>
                  <Select value={espSel?.resource_id || 'none'} onValueChange={(v) => setEspSel(recursos.espacios.find(e => e.resource_id === v) || null)}>
                    <SelectTrigger className="h-8 text-xs" data-testid="conf-esp"><SelectValue placeholder="Cualquiera" /></SelectTrigger>
                    <SelectContent>
                      {recursos.espacios.map(e => (
                        <SelectItem key={e.resource_id} value={e.resource_id} disabled={!e.libre}>
                          {e.nombre}{!e.libre ? ' (ocupado)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Oportunidad opcional — nunca requisito para agendar */}
              {catalogo.length > 0 && (
                <div className="border border-[#E2E8F0] rounded-lg p-2.5 space-y-2">
                  <label className="flex items-center gap-2 text-xs font-medium text-[#334155] cursor-pointer">
                    <input type="checkbox" checked={creaOpp} onChange={(e) => setCreaOpp(e.target.checked)} data-testid="drawer-crear-opp" />
                    <Package className="w-3.5 h-3.5" /> Registrar también una oportunidad de venta (opcional)
                  </label>
                  {creaOpp && (
                    <div className="space-y-2">
                      <Select value={oppItem || 'none'} onValueChange={(v) => {
                        if (v === 'none') { setOppItem(''); return; }
                        setOppItem(v);
                        const it = catalogo.find(i => i.id === v);
                        if (it?.price_min != null) setOppValor(String(it.price_min));
                      }}>
                        <SelectTrigger className="h-8 text-xs" data-testid="drawer-opp-item"><SelectValue placeholder="Producto o servicio…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Ninguno</SelectItem>
                          {catalogo.map(i => (
                            <SelectItem key={i.id} value={i.id}>
                              {i.name}{i.price_min != null ? ` — ${i.currency || 'MXN'} ${Number(i.price_min).toLocaleString('es-MX')}` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input className="h-8 text-xs" type="number" placeholder="Valor estimado"
                             value={oppValor} onChange={(e) => setOppValor(e.target.value)} />
                    </div>
                  )}
                </div>
              )}

              <Button className="w-full" disabled={guardando || cargandoRec} onClick={() => confirmar(slotSel)} data-testid="conf-agendar">
                <Check className="w-4 h-4 mr-1" /> Confirmar {L('appointment', 'cita').toLowerCase()}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
